import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControl,
  ListItemText,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import ClientAutocomplete from "../../components/clientAutocomplete";
import AnalysisPageTopbar from "../../components/metrics/AnalysisPageTopbar";
import SalesPerformancePageLoading from "../../components/metrics/SalesPerformancePageLoading";
import AutocompleteWithPagination from "../../components/autocomplete-with-pagination";
import { orderService } from "../../utils/services/orderService";
import {
  MONO_FONT,
  PAGE_FONT,
  SectionHead,
  headCellSx,
  numCellSx,
  textCellSx,
  useMetricsUi,
} from "../../components/metrics/metricsUi";
import {
  formatCount,
  formatCurrency,
} from "../../components/metrics/metricsFormat";
import { fieldSx } from "../manage-catalog/constants";
import { isMobile } from "../../globalStyles/mobile";
import { salesHistoryService } from "../../utils/services/salesHistoryService";
import AnalyticsWarehousePicker from "../../components/analytics-warehouse-picker";
import {
  normalizeWarehouseSelection,
  selectedWarehouseIds,
  warehouseIdsKey,
  warehouseOptionLabel,
  warehouseSelectionSummary,
} from "../../utils/analyticsWarehouses";
import SalesHistoryDetailDialog from "./SalesHistoryDetailDialog";

const FIELD_HEIGHT = 36;
const fieldControlSx = {
  ...fieldSx,
  "& .MuiOutlinedInput-root": {
    ...fieldSx["& .MuiOutlinedInput-root"],
    height: FIELD_HEIGHT,
    fontFamily: PAGE_FONT,
    fontSize: 13,
    borderRadius: "8px",
  },
};

const toolbarButtonSx = {
  height: FIELD_HEIGHT,
  fontSize: 13,
  fontWeight: 600,
  borderRadius: "8px",
  textTransform: "none",
};

const autocompleteInputSx = {
  ...fieldControlSx,
  margin: 0,
  mt: 0,
  mb: 0,
  "&.MuiFormControl-marginDense": { margin: 0 },
  "& .MuiInputLabel-root": { display: "none" },
  "& .MuiOutlinedInput-root": {
    ...fieldControlSx["& .MuiOutlinedInput-root"],
    backgroundColor: "var(--bg-paper)",
  },
};

const todayKey = () => new Date().toISOString().slice(0, 10);
const addUtcYears = (dateKey, years) => {
  const d = new Date(`${dateKey}T00:00:00.000Z`);
  d.setUTCFullYear(d.getUTCFullYear() + years);
  return d.toISOString().slice(0, 10);
};

const defaultDateRange = () => {
  const end = todayKey();
  return { start_date: addUtcYears(end, -5), end_date: end };
};

const formatMoney = (value) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value) || 0);

const formatDateShort = (dayKey) => {
  if (!dayKey) return "—";
  const d = new Date(`${dayKey}T00:00:00.000Z`);
  if (Number.isNaN(d.getTime())) return dayKey;
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
    timeZone: "UTC",
  });
};

const catalogOptionLabel = (item) => {
  if (!item) return "";
  if (item.label) return item.label;
  if (item.kind === "sku") {
    return item.name ? `SKU · ${item.sku} (${item.name})` : `SKU · ${item.sku}`;
  }
  if (item.kind === "product") {
    return item.sku_count > 1
      ? `Product · ${item.name} · ${item.sku_count} SKUs`
      : `Product · ${item.name}`;
  }
  return "";
};

const FieldLabel = ({ children, colors }) => (
  <Typography
    sx={{
      fontSize: 11,
      fontWeight: 600,
      color: colors.textTertiary,
      letterSpacing: "0.03em",
      textTransform: "uppercase",
      mb: 0.5,
      lineHeight: 1,
    }}
  >
    {children}
  </Typography>
);

const ToolbarField = ({ label, colors, children }) => (
  <Box sx={{ display: "flex", flexDirection: "column" }}>
    <FieldLabel colors={colors}>{label}</FieldLabel>
    {children}
  </Box>
);

const KpiCard = ({ label, value, sub, tooltip, ui }) => (
  <Box
    sx={{
      ...ui.card,
      padding: "14px 16px 13px",
      minHeight: 104,
      height: "100%",
      display: "flex",
      flexDirection: "column",
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.75 }}>
      <Typography
        sx={{
          fontSize: 11.5,
          fontWeight: 600,
          color: ui.colors.textSecondary,
          letterSpacing: "0.02em",
        }}
      >
        {label}
      </Typography>
      {tooltip && (
        <Tooltip title={tooltip} arrow>
          <InfoOutlinedIcon sx={{ fontSize: 13, color: ui.colors.textTertiary }} />
        </Tooltip>
      )}
    </Box>
    <Typography
      sx={{
        fontSize: 24,
        fontWeight: 650,
        letterSpacing: "-0.01em",
        fontVariantNumeric: "tabular-nums",
        color: ui.colors.textPrimary,
        lineHeight: 1.15,
      }}
    >
      {value}
    </Typography>
    <Box sx={{ mt: "auto", pt: 0.9, minHeight: 16 }}>
      {sub != null && (
        <Typography sx={{ fontSize: 11.5, color: ui.colors.textTertiary }}>
          {sub}
        </Typography>
      )}
    </Box>
  </Box>
);

const COLUMN_DEFS = [
  { id: "product", label: "Product", align: "left" },
  { id: "sku", label: "SKU", align: "left" },
  { id: "units_sold", label: "Units Sold", align: "right", sort: "units_sold" },
  { id: "gross_sales", label: "Sales $", align: "right", sort: "gross_sales", money: true },
  { id: "days_with_sales", label: "Days With Sales", align: "right", sort: "days_with_sales" },
  { id: "first_sale", label: "First Sale", align: "left", date: true, sort: "first_sale" },
  { id: "last_sale", label: "Last Sale", align: "left", date: true, sort: "last_sale" },
];

const SalesHistoryPage = () => {
  const ui = useMetricsUi();
  const { colors } = ui;
  const storedClientId = localStorage.getItem("client_id");
  const defaults = defaultDateRange();
  const [selectedClientData, setSelectedClientData] = useState(null);
  const [startDate, setStartDate] = useState(defaults.start_date);
  const [endDate, setEndDate] = useState(defaults.end_date);
  // [] = All Warehouses (no filter — every warehouse of the client); else the
  // selected set, attributed server-side as one scope.
  const [selectedWarehouses, setSelectedWarehouses] = useState([]);
  // Coalesced Order Source values (`source_channel || oms_provider`), the same
  // options Sales Analysis and Order Management use. Empty = all channels.
  const [sourceChannelOptions, setSourceChannelOptions] = useState([]);
  const [selectedSourceChannels, setSelectedSourceChannels] = useState([]);
  const [catalogFilter, setCatalogFilter] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [payload, setPayload] = useState(null);
  const [reloadNonce, setReloadNonce] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [sortField, setSortField] = useState("units_sold");
  const [sortDir, setSortDir] = useState("desc");
  const [detailRow, setDetailRow] = useState(null);

  const clientId = storedClientId || selectedClientData?._id;
  // Canonical (sorted, deduplicated) ids: stable effect dependency and params.
  const warehouseKey = warehouseIdsKey(selectedWarehouseIds(selectedWarehouses));
  const warehouseIds = useMemo(
    () => (warehouseKey ? warehouseKey.split(",") : []),
    [warehouseKey],
  );
  const invalidRange = !startDate || !endDate || startDate > endDate;

  useEffect(() => {
    if (!clientId) {
      setSourceChannelOptions([]);
      setSelectedSourceChannels([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await orderService.fetchOrderSource("", clientId, 50, 0);
        if (cancelled) return;
        const options = (data?.data || [])
          .map((row) => row?._id)
          .filter((value) => value != null && value !== "");
        setSourceChannelOptions(options);
        setSelectedSourceChannels((prev) => {
          const next = prev.filter((value) => options.includes(value));
          if (
            next.length === prev.length &&
            next.every((value, index) => value === prev[index])
          ) {
            return prev;
          }
          return next;
        });
      } catch {
        if (!cancelled) {
          setSourceChannelOptions([]);
          setSelectedSourceChannels([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [clientId]);

  useEffect(() => {
    if (!clientId || invalidRange) {
      setPayload(null);
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await salesHistoryService.fetch({
          clientId,
          startDate,
          endDate,
          warehouseIds,
          sku:
            catalogFilter?.kind === "sku" ? catalogFilter.sku : undefined,
          productId:
            catalogFilter?.kind === "product"
              ? catalogFilter.product_id || catalogFilter._id
              : undefined,
          skip: page * rowsPerPage,
          limit: rowsPerPage,
          sortField,
          sortDir,
          sourceChannels: selectedSourceChannels,
        });
        if (!cancelled) setPayload(result);
      } catch (err) {
        if (!cancelled) {
          setPayload(null);
          setError(
            err?.response?.data?.message ||
              err?.message ||
              "Unable to load sales history.",
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    clientId,
    startDate,
    endDate,
    warehouseIds,
    selectedSourceChannels,
    catalogFilter?.kind,
    catalogFilter?.sku,
    catalogFilter?.product_id,
    catalogFilter?._id,
    reloadNonce,
    page,
    rowsPerPage,
    sortField,
    sortDir,
    invalidRange,
  ]);

  const pageLoading = Boolean(clientId) && loading;
  const showChannelFilter = sourceChannelOptions.length >= 2;
  const warehouseLabel = warehouseSelectionSummary(selectedWarehouses, {
    allLabel: "All Warehouses",
  });
  const warehouseLabelDetail =
    selectedWarehouses.length > 1
      ? `${warehouseLabel}: ${selectedWarehouses.map(warehouseOptionLabel).join(", ")}`
      : warehouseLabel;

  const toggleSort = (field) => {
    if (!field) return;
    if (sortField === field) {
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("desc");
    }
    setPage(0);
  };

  const cellValue = (row, col) => {
    const value = row[col.id];
    if (col.money) return formatMoney(value);
    if (col.date) return formatDateShort(value);
    if (col.id === "units_sold" || col.id === "days_with_sales") {
      return formatCount(value);
    }
    if (col.id === "product") return value || "—";
    return value || "—";
  };

  return (
    <Box
      sx={{
        backgroundColor: ui.darkMode ? colors.bgPrimary : "#F5F6F8",
        minHeight: "100%",
        fontFamily: PAGE_FONT,
      }}
    >
      <Box
        sx={{
          maxWidth: 1480,
          mx: "auto",
          padding: isMobile() ? "14px 12px 40px" : "22px 28px 56px",
        }}
      >
        <AnalysisPageTopbar
          badge="SP"
          title="Sales by Product"
          clientName={selectedClientData?.business_name || selectedClientData?.name}
          right={
            <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: 1.25 }}>
              {!storedClientId && (
                <ToolbarField label="Client" colors={colors}>
                  <ClientAutocomplete
                    width={200}
                    selectedClient={selectedClientData}
                    onClientChange={(client) => {
                      setSelectedClientData(client);
                      setCatalogFilter(null);
                      setPage(0);
                    }}
                    hideLabel
                  />
                </ToolbarField>
              )}
              <ToolbarField label="Start Date" colors={colors}>
                <TextField
                  type="date"
                  size="small"
                  value={startDate}
                  onChange={(e) => {
                    setStartDate(e.target.value);
                    setPage(0);
                  }}
                  InputLabelProps={{ shrink: true }}
                  error={invalidRange}
                  sx={{ ...fieldControlSx, width: 152 }}
                />
              </ToolbarField>
              <ToolbarField label="End Date" colors={colors}>
                <TextField
                  type="date"
                  size="small"
                  value={endDate}
                  onChange={(e) => {
                    setEndDate(e.target.value);
                    setPage(0);
                  }}
                  InputLabelProps={{ shrink: true }}
                  error={invalidRange}
                  sx={{ ...fieldControlSx, width: 152 }}
                />
              </ToolbarField>
              <ToolbarField label="Search SKU or Product" colors={colors}>
                <Box sx={{ width: 280 }}>
                  <AutocompleteWithPagination
                    hideLabel
                    width="100%"
                    containerSx={{ width: "100%" }}
                    selectedClient={{ _id: clientId }}
                    selectedItem={catalogFilter}
                    onDataChange={(item) => {
                      setCatalogFilter(item || null);
                      setPage(0);
                    }}
                    optionKey={catalogOptionLabel}
                    placeholder="Search SKU or Product"
                    apiEndPoint={(limit, skip, search, client) =>
                      salesHistoryService.searchCatalog({
                        clientId: client,
                        q: search,
                        skip,
                        limit,
                      })
                    }
                    disabled={loading || !clientId}
                    freeSolo={false}
                    inputSx={autocompleteInputSx}
                  />
                </Box>
              </ToolbarField>
              <ToolbarField label="Warehouse" colors={colors}>
                <AnalyticsWarehousePicker
                  clientId={clientId}
                  mode="multi"
                  value={selectedWarehouses}
                  onChange={(next) => {
                    setSelectedWarehouses(normalizeWarehouseSelection(next));
                    setPage(0);
                  }}
                  disabled={loading || !clientId}
                  width={240}
                  height={FIELD_HEIGHT}
                  ariaLabel="Warehouse"
                  triggerSx={{
                    fontFamily: PAGE_FONT,
                    fontSize: 13,
                    fontWeight: 400,
                    borderRadius: "8px",
                  }}
                />
              </ToolbarField>
              {showChannelFilter && (
                <ToolbarField label="Sales Channel" colors={colors}>
                  <FormControl size="small" sx={{ width: 200 }}>
                    <Select
                      multiple
                      displayEmpty
                      value={selectedSourceChannels}
                      onChange={(e) => {
                        const next = e.target.value;
                        setSelectedSourceChannels(
                          typeof next === "string" ? next.split(",") : next,
                        );
                        setPage(0);
                      }}
                      disabled={loading || !clientId}
                      renderValue={(selected) =>
                        !selected?.length
                          ? "All Channels"
                          : selected.length === 1
                            ? selected[0]
                            : `${selected.length} channels`
                      }
                      sx={{
                        backgroundColor: colors.bgPaper,
                        fontSize: 13,
                        fontWeight: 600,
                        borderRadius: "8px",
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: ui.borderStrong,
                        },
                      }}
                    >
                      {sourceChannelOptions.map((option) => (
                        <MenuItem key={option} value={option} sx={{ fontSize: 13 }}>
                          <Checkbox
                            size="small"
                            checked={selectedSourceChannels.includes(option)}
                          />
                          <ListItemText
                            primary={option}
                            primaryTypographyProps={{ fontSize: 13 }}
                          />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </ToolbarField>
              )}
              <Button
                variant="outlined"
                onClick={() => setReloadNonce((n) => n + 1)}
                disabled={loading || !clientId || invalidRange}
                startIcon={<RefreshIcon sx={{ fontSize: 16 }} />}
                sx={{
                  ...toolbarButtonSx,
                  borderColor: ui.borderStrong,
                  color: colors.textSecondary,
                  backgroundColor: colors.bgPaper,
                }}
              >
                Refresh
              </Button>
            </Box>
          }
        />

        <Typography
          sx={{
            fontSize: 13,
            color: colors.textSecondary,
            mb: 2,
            mt: -1,
            lineHeight: 1.5,
          }}
        >
          Historical SKU sales performance for the selected period and warehouse
          scope.
        </Typography>

        {!clientId && (
          <Alert severity="info" sx={{ mb: 2, borderRadius: "10px" }}>
            Select a client to load sales history.
          </Alert>
        )}
        {invalidRange && clientId && (
          <Alert severity="warning" sx={{ mb: 2, borderRadius: "10px" }}>
            Start date must not be after end date.
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: "10px" }}>
            {error}
          </Alert>
        )}

        {pageLoading && (
          <SalesPerformancePageLoading
            title="Loading sales history..."
            kpiCount={4}
          />
        )}

        {!pageLoading && payload && (
          <>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: isMobile()
                  ? "repeat(2, 1fr)"
                  : "repeat(auto-fit, minmax(190px, 1fr))",
                gap: 1.5,
                mb: 2.5,
              }}
            >
              <KpiCard
                ui={ui}
                label="Total Units Sold"
                value={formatCount(payload.totals?.units_sold)}
                sub={warehouseLabel}
              />
              <KpiCard
                ui={ui}
                label="Gross Sales $"
                value={formatCurrency(payload.totals?.gross_sales)}
                sub="Path A gross merchandise"
                tooltip={payload.meta?.sales_definition_description}
              />
              <KpiCard
                ui={ui}
                label="SKU Count"
                value={formatCount(payload.totals?.sku_count)}
                sub="SKUs with units sold"
              />
              <KpiCard
                ui={ui}
                label="Sales Coverage"
                value={
                  payload.coverage?.first_sale && payload.coverage?.last_sale
                    ? `${formatDateShort(payload.coverage.first_sale)} – ${formatDateShort(payload.coverage.last_sale)}`
                    : "—"
                }
                sub="First and last sale in range"
              />
            </Box>

            <Box sx={{ ...ui.card, px: 2.25, pt: 1.75, pb: 1, mb: 2, overflowX: "auto" }}>
              <SectionHead
                title="SKU sales"
                subtitle={`${warehouseLabelDetail} · ${payload.start_date} – ${payload.end_date}. Click a SKU for history and insights.`}
                center
              />
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {COLUMN_DEFS.map((col) => (
                      <TableCell
                        key={col.id}
                        sx={headCellSx(ui, {
                          textAlign: col.align === "right" ? "right" : "left",
                        })}
                      >
                        {col.sort ? (
                          <TableSortLabel
                            active={sortField === col.sort}
                            direction={sortField === col.sort ? sortDir : "desc"}
                            onClick={() => toggleSort(col.sort)}
                          >
                            {col.label}
                          </TableSortLabel>
                        ) : (
                          col.label
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(payload.data || []).length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={COLUMN_DEFS.length} sx={textCellSx(ui)}>
                        {payload.filter?.type
                          ? "No matching sales history."
                          : "No SKU sales in this range."}
                      </TableCell>
                    </TableRow>
                  ) : (
                    payload.data.map((row) => (
                      <TableRow
                        key={row.sku}
                        hover
                        onClick={() => setDetailRow(row)}
                        sx={{ cursor: "pointer" }}
                      >
                        {COLUMN_DEFS.map((col) => (
                          <TableCell
                            key={col.id}
                            sx={
                              col.align === "right"
                                ? numCellSx(ui)
                                : textCellSx(ui)
                            }
                          >
                            {cellValue(row, col)}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={payload.total || 0}
                page={page}
                onPageChange={(_e, next) => setPage(next)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(Number(e.target.value));
                  setPage(0);
                }}
                rowsPerPageOptions={[10, 25, 50, 100]}
              />
            </Box>

            <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${colors.border}` }}>
              <Typography
                sx={{
                  fontSize: 11.5,
                  fontWeight: 700,
                  color: colors.textTertiary,
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  mb: 1,
                }}
              >
                Calculation reference
              </Typography>
              <Typography sx={{ fontSize: 12, color: colors.textSecondary, lineHeight: 1.7 }}>
                <Box component="span" sx={{ color: colors.textPrimary, fontWeight: 600 }}>
                  Units Sold / Sales $
                </Box>
                {" — "}
                {payload.meta?.sales_definition_description ||
                  "Gross merchandise from non-cancelled orders: line price × quantity."}
              </Typography>
              <Typography sx={{ fontSize: 12, color: colors.textSecondary, lineHeight: 1.7 }}>
                <Box component="span" sx={{ color: colors.textPrimary, fontWeight: 600 }}>
                  Warehouse
                </Box>
                {" — "}
                {payload.warehouse_scope === "warehouse"
                  ? "Attributed to the selected warehouse(s) using order.warehouse or matching shipment warehouse/items; each order counts once across the selection. Cancelled orders stay excluded."
                  : "All Warehouses is a client SKU total. Split shipments are counted once from the order line, not once per warehouse."}
              </Typography>
              <Typography
                sx={{ mt: 1.5, fontSize: 11, color: colors.textTertiary, fontFamily: MONO_FONT }}
              >
                {payload.meta?.sales_definition} · {payload.meta?.warehouse_attribution}
              </Typography>
            </Box>
          </>
        )}
      </Box>
      <SalesHistoryDetailDialog
        open={Boolean(detailRow)}
        sku={detailRow?.sku}
        productHint={detailRow?.product}
        clientId={clientId}
        startDate={startDate}
        endDate={endDate}
        warehouseIds={warehouseIds}
        warehouseLabel={warehouseLabelDetail}
        sourceChannels={selectedSourceChannels}
        sourceOptions={sourceChannelOptions}
        onClose={() => setDetailRow(null)}
        ui={ui}
      />
    </Box>
  );
};

export default SalesHistoryPage;
